/* ================================================================
   NewJaisa — Student Experience
   File    : assets/student-mode.js
   Purpose : PDP orchestration ONLY.
             Renders only when ALL FOUR conditions are true:
               1. Customer entered through one of the six official
                  college landing pages (njc_student_landing cookie)
               2. A student session exists (njc_student / college_code
                  cookies, 48-hour expiry)
               3. The current product belongs to the configured shared
                  student collection.
                  Computed by checking the product's real collection
                  handles (rendered server-side in Liquid, since
                  Liquid can't read cookies) against
                  STUDENT_CONFIG[collegeCode].collectionHandle.
               4. The college code exists in STUDENT_CONFIG
             If any condition fails, the normal PDP renders exactly
             as it does today. Does not touch Product Form, Add to
             Cart, Buy Now, Breeze, Variant Selector, Gallery, Price,
             or Inventory.
   Exposes : window.StudentMode

   ARCHITECTURE CLEANUP (2026-07-10): all colleges use one shared
   student collection. The expected collection handle comes from
   STUDENT_CONFIG[collegeCode].collectionHandle, set explicitly per
   college in assets/student-config.js. STUDENT_CONFIG is the single
   source of truth for collection handles — this file contains no
   business naming logic. If a collection is ever renamed, or a 7th
   college is added, only STUDENT_CONFIG needs to change.

   When Student Mode is active, this file ALSO hides normal-customer
   marketing sections (HexaTrust, QC Report, the default coupon/promo
   card) that are not relevant to students and duplicate what the
   Student Experience block itself shows. Sections are hidden via
   classList (see .nj-student-hide in student-mode.css), never
   deleted, never touched via innerHTML, and no purchase-flow markup
   is included in this list.
   ================================================================ */

window.StudentMode = (function () {
  'use strict';

  var ROOT_SELECTOR = '[data-nj-student-root]';
  var COOKIE_EXPIRY_HOURS = 48;

  // Normal-customer marketing sections to hide once Student Mode is
  // confirmed active. Every selector here was verified against the
  // live DOM/source before being added — nothing guessed.
  var MARKETING_SELECTORS_TO_HIDE = [
    '.tsw-wrapper',            // HexaTrust — desktop grid (scroll_jacking.liquid)
    '.product-tabs-scrolling', // HexaTrust — mobile carousel (scroll_jacking.liquid)
    '.test-card-container',    // QC Report — persona comparison card (nj-qc-reports.liquid)
    '.qc-root',                 // QC Report — "See The Actual QC Results" accordion (nj-qc-report.liquid)
    '.promo-card-root',        // Default coupon / Flash Deal / Summer Mega Sale card (ott-warrenty.liquid)
    '.service-card'            // Default service locator card (service-locator.liquid)
  ];

  function log(details) {
    if (window.console && window.console.debug) {
      window.console.debug('[NJ Student Debug]', details);
    }
  }

  function hideNormalCustomerMarketing() {
    MARKETING_SELECTORS_TO_HIDE.forEach(function (selector) {
      var els = document.querySelectorAll(selector);
      for (var i = 0; i < els.length; i++) {
        els[i].classList.add('nj-student-hide');
      }
    });
  }

  function init() {
    var root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;

    var debug = {
      landingPage: false,
      cookieExists: false,
      cookieExpiryHours: COOKIE_EXPIRY_HOURS,
      collegeCode: null,
      expectedCollection: null,
      currentProductCollections: [],
      collectionMatch: false,
      studentMode: false,
      renderAllowed: false,
      renderBlockedReason: null
    };

    if (!window.NJStudentCookie || !window.STUDENT_CONFIG || !window.StudentRenderer) {
      debug.renderBlockedReason = 'required student scripts not loaded';
      log(debug);
      return;
    }

    // Condition 1: entered through a student landing page
    debug.landingPage = window.NJStudentCookie.hasLandingEntry();
    if (!debug.landingPage) {
      debug.renderBlockedReason = 'no student landing page entry on record (or expired)';
      log(debug);
      return;
    }

    // Condition 2: a student session (cookie) exists
    var collegeCode = window.NJStudentCookie.get();
    debug.cookieExists = !!collegeCode;
    debug.collegeCode = collegeCode;

    if (!collegeCode) {
      debug.renderBlockedReason = 'no student session cookie (missing or expired)';
      log(debug);
      return;
    }

    // Condition 4: college code must exist in STUDENT_CONFIG
    var config = window.STUDENT_CONFIG[collegeCode];
    if (!config) {
      debug.renderBlockedReason = 'college code not present in STUDENT_CONFIG';
      log(debug);
      return;
    }

    // Condition 3: product must belong to the configured student collection
    // (single source of truth: STUDENT_CONFIG[collegeCode].collectionHandle —
    // no business naming logic lives in this file anymore)
    var expectedHandle = config.collectionHandle;
    debug.expectedCollection = expectedHandle;

    var productCollections = [];
    try {
      productCollections = JSON.parse(root.getAttribute('data-nj-product-collections') || '[]');
    } catch (e) {
      productCollections = [];
    }
    debug.currentProductCollections = productCollections;
    debug.collectionMatch = productCollections.indexOf(expectedHandle) !== -1;

    if (!debug.collectionMatch) {
      debug.renderBlockedReason = 'product is not in ' + expectedHandle;
      log(debug);
      return;
    }

    debug.studentMode = true;

    window.StudentRenderer.render(config, root);
    root.hidden = false;
    root.setAttribute('data-nj-college', collegeCode);

    hideNormalCustomerMarketing();

    debug.renderAllowed = true;
    log(debug);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return {
    init: init
  };
})();
